/**
 * MarketoForm Component
 *
 * This React component is responsible for rendering a Marketo form within a Sitecore-powered environment.
 * It dynamically loads a form using the Marketo form API, applies styling based on the current theme
 * (light or dark), and integrates settings and data from Sitecore fields via GraphQL.
 *
 * Features include:
 * - Theming support for background color styling.
 * - Optional Sitecore-controlled layout and styling toggles (padding, text alignment).
 * - Optional email validation against a list of disallowed domains retrieved from Sitecore.
 * - Dynamic injection of validation config into the global scope for Marketo scripts.
 *
 * Dependencies:
 * - Sitecore JSS fields
 * - Marketo configuration (form ID, Munchkin ID, base URL)
 * - Custom hooks and utilities for theme handling, styling, and GraphQL data fetching
 */

import React from 'react';
// Hook to get the current theme (light/dark)
import { useTheme } from '../SharedUtils/hooks';
// Utility to get background color based on theme and fields
import { getBackgroundColor } from '../SharedUtils/themeColors';
// Utility to apply inline styles
import { addStyle } from '../SharedUtils/utils';
// Custom hook to load Marketo form and manage its lifecycle
import useMarketoForm from '../SharedUtils/hooks/useMarketoForm';
// Marketo-specific styles
import './marketoForm.css';
// Custom GraphQL hook to get field values from Sitecore
import { useGetSitecoreFields } from '../../../services/GraphQLService/useGetSitecoreFields';

const MarketoForm = ({ fields }) => {
  // Extract values from Sitecore fields
  const formId = fields.MarketoFormId?.value;
  let baseUrl = '';
  let munchkinId = '';
  const tyPage = fields.ThankYouPage?.value;

  // Get current theme (used to determine background color)
  const { isLight } = useTheme();
  const backgroundColor = getBackgroundColor(fields, isLight);

  // Build widget styles (dynamically applying background color)
  const widgetStyles = {};
  addStyle(widgetStyles, 'backgroundColor', backgroundColor);

  // Build widget class list based on field toggles
  const widgetClasses = ['o-widget', 'c-marketo', 'c-marketo-form', 'u-small-text-center'];
  if (fields?.TextAlignCentered?.value) {
    widgetClasses.push('u-text-center');
  }
  if (fields?.AddPadding?.value) {
    widgetClasses.push('has-padding');
  }

  // Fetch Marketo Form Settings from a Sitecore content item
  const { values } = useGetSitecoreFields(
    '/sitecore/content/Developer2-0/Settings/MarketoForm/MarketoFormSettings',
    ['BaseUrl', 'MunchkinId', 'FreeEmailDomains'],
    'en',
    fields?.EnableEmailValidation // only fetch if validation is enabled
  );

  if (values.BaseUrl && values.MunchkinId) {
    baseUrl = values.BaseUrl;
    munchkinId = values.MunchkinId;
  }

  // Load and render Marketo form using provided config
  const { elementId } = useMarketoForm({
    formId,
    baseUrl,
    munchkinId,
    tyPage,
    cleanStyles: true, // applies custom cleaning of default Marketo styles
  });

  // Build a <script> tag to configure Marketo email validation settings
  let emailDomainScriptTag = null;
  if (fields?.EnableEmailValidation && values.FreeEmailDomains) {
    let freeEmailDomainConfig = {
      errorMsg: 'Must be valid company email.',
      ignoreEmailDomain: values.FreeEmailDomains,
    };

    // Inject marketoConfig script into DOM with validation settings
    emailDomainScriptTag = (
      <script
        dangerouslySetInnerHTML={{
          __html: `var marketoConfig = ${JSON.stringify(freeEmailDomainConfig)};`,
        }}
      />
    );
  }

  return (
    <section className={widgetClasses.join(' ')} style={widgetStyles}>
      {/* Inject validation config if enabled */}
      {emailDomainScriptTag}

      {/* Container for Marketo form */}
      <div id={elementId}>
        <form id={`mktoForm_${formId}`}></form>
      </div>
    </section>
  );
};

export default MarketoForm;
